import { memo, useRef, useState } from "react";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { DisaggregatedMetric } from "../../services/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {
    Chart,
    DoughnutController,
    ArcElement,
    Tooltip,
    Legend,
  } from 'chart.js';
  

interface SimpleDonutChartProps {
    indicators: DisaggregatedMetric[];
    id: number;
    fillColor: string;
    remainColor: string;
    valueColor: string;
}

const SimpleDonutChart = memo(({ indicators, id, fillColor, remainColor, valueColor }: SimpleDonutChartProps) => {
    const [data, setData] = useState<number | null>(null);

    let title;

    useDeepCompareEffect(() => {
        if (!indicators) return;

        const chartData = indicators.find(data => data.id === id);

        // Reassign title as indicator name
        title = chartData?.name || '';
        
        if (!chartData) return;

        const version = chartData.versions[0];
        if (!version) return;

        const value = Math.round(version.value * 100);
        setData(value);

    }, [indicators]);


    // Register the plugin to all charts:
    // Register needed components
    Chart.register(DoughnutController, ArcElement, Tooltip, Legend, ChartDataLabels);   
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"doughnut"> | null>(null);

    useDeepCompareEffect(() => {
        const ctx = chartRef.current?.getContext("2d");
        if (ctx && data !== null) {
            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Create a donut chart with Chart.js
            chartInstance.current = new Chart(ctx, {
                type: "doughnut",
                data: {
                    labels: ['Value', 'Remaining'], // Define labels for the donut sections
                    datasets: [
                        {
                            data: [data, 100 - data], // Fill the donut based on the value and remaining percentage
                            backgroundColor: [fillColor, remainColor], // Set colors for value and remaining sections
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,  // Allows the chart to take the full height
                    cutout: '70%', // Adjust the size of the cutout (inner circle)
                    plugins: {
                        legend: {
                            display: false,
                        },
                        datalabels: {
                            display: false, // Disable default data labels
                        },
                        tooltip: {
                            enabled: false,
                        },
                    },
                },
                plugins: [
                    {
                        id: 'centerText',
                        afterDraw: function (chart) {
                            const ctx = chart.ctx;
                            const width = chart.width!;
                            const height = chart.height!;
                            ctx.restore();
                            ctx.font = '700 24px sans-serif';
                            ctx.textBaseline = 'middle';

                            ctx.fillStyle = valueColor;

                            const text = `${data}%`; // Display the value as a percentage
                            const textX = Math.round((width - ctx.measureText(text).width) / 2);
                            const textY = height / 2;

                            ctx.fillText(text, textX, textY);
                            ctx.save();
                        },
                    },
                ],
            });
        }
    }, [data, title]);

    return (
        <div className="flex justify-center items-center mx-2">
            <div className="h-20 w-20"> {/* Adjust height as needed */}
                <canvas ref={chartRef} className="w-full"/>
            </div>
        </div>
    );
});

export default SimpleDonutChart;